import {useEffect, useRef} from 'react';

import {recordRumAction, recordRumError} from '../../util/datadog/datadogRum';
import {type Line, type Timestep} from '../../util/plotHelpers/types';
import {type PartialZoom} from '../PanelRunsLinePlot/types';
import {type Domain} from './types';

/**
 * Ratio threshold for detecting domain/data mismatch.
 * If domainRange / dataRange exceeds this value (or its inverse),
 * we consider it a potential unit mismatch bug.
 *
 * 3x allows for reasonable padding/zoom while catching unit conversion
 * errors like seconds vs hours (3600x) or seconds vs minutes (60x).
 */
const MISMATCH_RATIO_THRESHOLD = 3;

type UseXDomainMismatchDetectionParams = {
  lines: Line[];
  panelID: string;
  queryZoom: PartialZoom;
  timestep: Timestep | undefined;
  xAxis: string;
  xDomain: Domain;
};

/**
 * Detects and reports mismatches between the x-axis domain and actual data bounds.
 *
 * This hook acts as a defensive check to catch bugs where domain boundaries
 * and data points end up in different units (e.g., domain in seconds but
 * data converted to hours). Such mismatches cause lines to render in a tiny
 * fraction of the visible plot area.
 *
 * The hook:
 * - Computes actual data bounds from line data (using first/last points for efficiency)
 * - Compares against the xDomain prop
 * - Fires a DD RUM event if a significant mismatch is detected
 * - Deduplicates alerts using a signature to avoid repeated fires for the same issue
 * - Skips detection when user has explicitly zoomed (intentional domain constraint)
 */
export function useXDomainMismatchDetection({
  lines,
  panelID,
  queryZoom,
  timestep,
  xAxis,
  xDomain,
}: UseXDomainMismatchDetectionParams): void {
  const lastAlertSignatureRef = useRef<string | null>(null);

  useEffect(() => {
    // the try/catch is important here as this is a detection hook and
    // any error in its implementation should not affect the app
    try {
      // Skip if user has explicitly set zoom bounds
      // the domain mismatch is probably intentional
      const userHasZoomed =
        queryZoom.xAxisMin != null || queryZoom.xAxisMax != null;
      if (userHasZoomed) {
        return;
      }

      // Skip if no lines or all lines are empty
      if (lines.length === 0) {
        return;
      }

      // Compute actual data bounds by examining line data.
      // x-axis data will be reliably sorted at this point
      // first and last points is sufficient
      let dataMinX = Infinity;
      let dataMaxX = -Infinity;

      const metrics = [];
      for (const line of lines) {
        if (line.data.length === 0) {
          continue;
        }
        metrics.push(line.metricName);
        const firstX = line.data[0].x;
        const lastX = line.data[line.data.length - 1].x;
        dataMinX = Math.min(dataMinX, firstX);
        dataMaxX = Math.max(dataMaxX, lastX);
      }

      // Skip if we couldn't compute valid bounds (all lines empty)
      if (!Number.isFinite(dataMinX) || !Number.isFinite(dataMaxX)) {
        return;
      }

      const domainRange = xDomain[1] - xDomain[0];
      const dataRange = dataMaxX - dataMinX;

      // Skip if data range is 0 (single point or all points at same x)
      // Can't compute meaningful ratio
      if (dataRange === 0) {
        return;
      }

      // Detect mismatch conditions:
      // 1. Domain is significantly larger than data range (data compressed into tiny area)
      // 2. Domain is significantly smaller than data range (data extends far beyond visible area)
      // 3. Data falls completely outside domain bounds (nothing visible)
      const ratio = domainRange / dataRange;
      const isMismatch =
        ratio > MISMATCH_RATIO_THRESHOLD ||
        ratio < 1 / MISMATCH_RATIO_THRESHOLD;
      const dataOutsideDomain = dataMaxX < xDomain[0] || dataMinX > xDomain[1];

      if (isMismatch || dataOutsideDomain) {
        // Create signature to deduplicate alerts - we don't want to fire repeatedly
        // for the same mismatch on every render
        const alertSignature = [
          xDomain[0].toFixed(2),
          xDomain[1].toFixed(2),
          dataMinX.toFixed(2),
          dataMaxX.toFixed(2),
        ].join('-');

        if (lastAlertSignatureRef.current !== alertSignature) {
          lastAlertSignatureRef.current = alertSignature;

          recordRumAction('backend_line_alerts:x_domain_mismatch', {
            dataMaxX,
            dataMinX,
            dataOutsideDomain,
            dataRange,
            domainRange,
            lineCount: lines.length,
            linesHaveDomain: lines.some(l => l.domain !== undefined),
            metrics,
            ratio,
            timestep,
            xAxis,
            xDomain,
          });
        }
      }
    } catch (error) {
      recordRumError(error, {
        message: 'backend_line_alerts: Error in useXDomainMismatchDetection',
        source: 'useXDomainMismatchDetection',
      });
    }
  }, [lines, xDomain, xAxis, timestep, queryZoom, panelID]);
}
